import { infiniteQueryOptions, useMutation, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import { invoiceAccountReceivablePaymentDetailIncludeFragments } from './invoice-ar-payment-includes';
import type { InvoiceCreateSchema, InvoiceFormSchema, InvoiceMutationSchema } from './schemas';
import type { InvoicePaymentsSyncBody } from './schemas/invoice-payments/invoice-payments-sync-mutation-schema';
import type { InvoiceQueryAllSchema, InvoiceQuerySchema } from './schemas/invoices/invoice-query-schema';
import type {
  SendInvoiceEmailBodySchema,
  SendInvoiceEmailResponseSchema,
} from './schemas/send-invoice-email-mutation-schema';

const invoicesListStaleTimeMs = 5 * 60 * 1000;
const invoicesListGcTimeMs = 30 * 60 * 1000;

export const includeKeysBase = ['brand.icon', 'partner.icon', 'client.logo', 'created_by', 'invoice_batch'];

/** agreement_links data required by invoice-mode bottom split (list-cache rendering). */
export const includeKeysListAgreementLinks = [
  'agreement_links.invoice_deliverables',
  'agreement_links.invoice_deliverables.price_frequency',
  'agreement_links.agreement.billing_term',
  'agreement_links.agreement.billing_contact.emails',
  'agreement_links.agreement.billing_contact.profile_image',
  'agreement_links.agreement.billing_email_contacts.emails',
  'agreement_links.agreement.billing_email_contacts.profile_image',
  'agreement_links.agreement.addresses',
  'agreement_links.agreement.addresses.country',
  'agreement_links.agreement.addresses.state',
  'agreement_links.agreement.primary_domains',
];

/** PDF file relations for invoice list rows (actions column + ⌘D / Ctrl+D shortcut). */
export const includeKeysListPdfs = ['digital_invoices_pdf', 'printable_invoices_pdf'];

/** Includes for single invoice detail (line items + tabs). */
const includeKeysDetail = [
  ...includeKeysBase,
  'digital_invoices_pdf',
  'printable_invoices_pdf',
  ...includeKeysListAgreementLinks,
  ...invoiceAccountReceivablePaymentDetailIncludeFragments,
  'latest_email_history',
];
const includeKeysDetailStr = includeKeysDetail.join(',');

const includeKeysListStr = [
  ...includeKeysBase,
  ...includeKeysListAgreementLinks,
  ...includeKeysListPdfs,
  'account_receivables',
  'latest_email_history',
].join(',');

export function getInvoicesQuery(apiParamStr = '?per_page=100&page=1') {
  return {
    queryKey: ['INVOICES', apiParamStr],
    staleTime: invoicesListStaleTimeMs,
    gcTime: invoicesListGcTimeMs,
    queryFn: async () => {
      const res = await apiClient.get<InvoiceQueryAllSchema>({
        endpoint: `/api/v1/invoices?include=${includeKeysListStr}`,
        params: `${apiParamStr}`,
      });

      return res;
    },
  };
}

export function useGetInvoices(apiParamStr: string) {
  return useSuspenseQuery(getInvoicesQuery(apiParamStr));
}

export function getInvoicesInfiniteQueryOptions(apiParamStr = '?per_page=50') {
  return infiniteQueryOptions<InvoiceQueryAllSchema>({
    queryKey: ['INVOICES', apiParamStr],
    initialPageParam: 1,
    staleTime: invoicesListStaleTimeMs,
    gcTime: invoicesListGcTimeMs,
    queryFn: ({ pageParam }) =>
      apiClient.get<InvoiceQueryAllSchema>({
        endpoint: `/api/v1/invoices?include=${includeKeysListStr}`,
        params: `${apiParamStr}&page=${pageParam}`,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
  });
}

export function useGetInvoicesInfinite(apiParamStr: string) {
  return useSuspenseInfiniteQuery(getInvoicesInfiniteQueryOptions(apiParamStr));
}

export const invalidateQueryAllInvoicesAndBatchObjects = () => {
  queryClient.invalidateQueries({ queryKey: ['INVOICES'] });
  queryClient.invalidateQueries({ queryKey: ['INVOICE_BATCHES'] });
};

export function useGetInvoiceSuspense(id: string) {
  return useSuspenseQuery(getInvoiceQuery(id));
}

export function getInvoiceQuery(id: string) {
  return {
    queryKey: ['INVOICE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InvoiceQuerySchema }>({
        endpoint: `/api/v1/invoices/${id}`,
        params: `?include=${includeKeysDetailStr}`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateInvoiceQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['INVOICE', id] });
};

export const useGetInvoicesMutation: UseControlledMutation<InvoiceQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVOICES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InvoiceQueryAllSchema>({
            endpoint: '/api/v1/invoices',
            params: { search, per_page, include: 'brand.icon,partner.icon,client.logo' },
          });

          return orderBy(res?.data ?? [], ['invoice_number']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: InvoiceQuerySchema }, unknown, InvoiceCreateSchema>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: '/api/v1/invoices/',
        payload,
      });
    },
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InvoiceQuerySchema }, ApiError, InvoiceMutationSchema>({
    mutationFn: async ({ id: bId, ...payload }) => {
      return apiClient.request({
        endpoint: `/api/v1/invoices/${id || bId}`,
        method: 'PUT',
        payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'invoices', id] });
      invalidateQueryAllInvoicesAndBatchObjects();
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: InvoiceQuerySchema }, ApiError, Partial<InvoiceMutationSchema>>({
    mutationFn: async ({ id, ...payload }) => {
      return apiClient.request({
        endpoint: `/api/v1/invoices/${id}`,
        method: 'PATCH',
        payload,
      });
    },
  });

export const useSyncInvoicePaymentsMutation = (invoiceId: string) =>
  useMutation<{ data: InvoiceQuerySchema }, ApiError, InvoicePaymentsSyncBody>({
    mutationFn: async (payload) =>
      apiClient.request({
        endpoint: `/api/v1/invoices/${invoiceId}/payments/sync`,
        method: 'POST',
        payload,
      }),
    onSuccess: () => {
      // Silent background refresh for active invoice detail observers.
      queryClient.invalidateQueries({ queryKey: ['INVOICE', invoiceId], exact: true, refetchType: 'active' });
      invalidateQueryAllInvoicesAndBatchObjects();
    },
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVOICE_DELETE'],
    mutationFn: async ({ id }) => {
      return apiClient.request({
        endpoint: `/api/v1/invoices/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'invoices', id] });
    },
  });

export const useCreateInvoiceNumberMutation = () =>
  useMutation<{ invoice_number: string }, ApiError, { invoice_date: string }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/invoices/generate-invoice-number',
        payload,
      }),
  });

// BULK OPERATIONS

export const useInvoicesBulkCreateMutation = () =>
  useMutation<InvoiceFormSchema[], ApiError, { items: InvoiceCreateSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/invoices/bulk-store',
        payload,
      }),
  });

export const useInvoicesBulkUpdateMutation = () =>
  useMutation<{ data: InvoiceQuerySchema }, ApiError, { items: InvoiceMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/invoices/bulk-update`,
        method: 'POST',
        payload,
      }),
  });

export const useInvoicesBulkDeleteMutation = () =>
  useMutation<{ data: InvoiceQuerySchema }, ApiError, { items: number[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/invoices/bulk-delete`,
        method: 'POST',
        payload,
      }),
  });

/** Flip to true when POST /api/v1/invoices/:id/send-email is ready on the backend. */
export const INVOICE_SEND_EMAIL_API_ENABLED = true;

/** Flip to false if scheduled invoice email sending is not yet supported by the backend. */
export const INVOICE_SCHEDULE_EMAIL_ENABLED = true;

export const useSendInvoiceEmailMutation = () =>
  useMutation<SendInvoiceEmailResponseSchema, ApiError, SendInvoiceEmailBodySchema & { invoiceId: number }>({
    mutationFn: async ({ invoiceId, ...payload }) => {
      if (!INVOICE_SEND_EMAIL_API_ENABLED) {
        throw new Error('Send invoice email is temporarily unavailable.');
      }

      return apiClient.request({
        endpoint: `/api/v1/invoices/${invoiceId}/send-email`,
        method: 'POST',
        payload,
      });
    },
    onSuccess: (_, { invoiceId }) => {
      invalidateQueryAllInvoicesAndBatchObjects();
      queryClient.invalidateQueries({ queryKey: ['EMAILS_HISTORY_BY_INVOICE', invoiceId] });
    },
  });
